<template>
  <div class="flex flex-col google-address-search">
    <label v-if="label" class="text-sm field-label">
      {{ label }}
    </label>
    <div class="search-wrapper">
      <input
        v-model="searchQuery"
        type="text"
        class="bordered search-input"
        :placeholder="placeholder"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <svg v-if="loading" class="search-spinner" viewBox="0 0 24 24">
        <circle
          class="spinner-circle"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
        />
      </svg>
    </div>

    <!-- Predictions Dropdown -->
    <div
      v-if="showPredictions && predictions.length > 0"
      class="predictions-list"
    >
      <div
        v-for="prediction in predictions"
        :key="prediction.place_id"
        class="prediction-item"
        @mousedown.prevent="selectPrediction(prediction)"
      >
        <svg
          class="location-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
        </svg>
        <div class="prediction-text">
          <div class="main-text">
            {{ prediction.structuredFormatting.mainText }}
          </div>
          <div class="secondary-text">
            {{ prediction.structuredFormatting.secondaryText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getAddressAutocomplete,
  getPlaceDetails
} from "@/api/store/GooglePlacesService";

export default {
  name: "GoogleAddressSearch",
  props: {
    value: {
      type: String,
      default: ""
    },
    label: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: "Start typing your address..."
    },
    minChars: {
      type: Number,
      default: 3
    },
    debounceMs: {
      type: Number,
      default: 300
    },
    countries: {
      type: Array,
      default: () => ["us", "ca", "ve", "cr"]
    }
  },
  data() {
    return {
      searchQuery: "",
      predictions: [],
      showPredictions: false,
      loading: false,
      debounceTimer: null
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.searchQuery = newVal || "";
      }
    }
  },
  methods: {
    onInput() {
      // Emit input change
      this.$emit("input", this.searchQuery);

      // Clear previous timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      // If query is too short, clear predictions
      if (this.searchQuery.length < this.minChars) {
        this.predictions = [];
        this.showPredictions = false;
        return;
      }

      // Debounce the API call
      this.debounceTimer = setTimeout(() => {
        this.fetchPredictions();
      }, this.debounceMs);
    },

    onFocus() {
      if (this.predictions.length > 0) {
        this.showPredictions = true;
      }
    },

    onBlur() {
      // Delay hiding to allow click events
      setTimeout(() => {
        this.showPredictions = false;
      }, 200);
    },

    async fetchPredictions() {
      if (!this.searchQuery) {
        this.predictions = [];
        return;
      }

      this.loading = true;

      try {
        const countriesParam = this.countries
          .map(c => `country:${c}`)
          .join("|");

        const data = await getAddressAutocomplete(
          this.searchQuery,
          "address",
          countriesParam
        );

        if (data.status === "OK" && data.predictions) {
          this.predictions = data.predictions;
          this.showPredictions = true;
        } else if (data.status === "ZERO_RESULTS") {
          this.predictions = [];
          this.showPredictions = false;
        } else {
          console.warn("Autocomplete failed:", data.status);
          this.predictions = [];
          this.showPredictions = false;
        }
      } catch (error) {
        console.error("Error fetching address predictions:", error);
        this.predictions = [];
        this.showPredictions = false;
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },

    async selectPrediction(prediction) {
      this.showPredictions = false;
      this.loading = true;

      try {
        const data = await getPlaceDetails(
          prediction.placeId,
          "address_components,formatted_address"
        );

        if (data.status === "OK" && data.result) {
          const addressData = this.parseAddressComponents(
            data.result.addressComponents
          );

          // Update search query with the street address
          this.searchQuery = addressData.street1 || "";
          this.$emit("input", this.searchQuery);

          // Emit the complete address data
          this.$emit("address-selected", addressData);
        } else {
          console.error("Failed to get place details:", data.status);
          this.$emit(
            "error",
            new Error(`Failed to get place details: ${data.status}`)
          );
        }
      } catch (error) {
        console.error("Error fetching place details:", error);
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },

    parseAddressComponents(components) {
      const addressData = {
        street1: "",
        street2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        street_number: "",
        route: ""
      };

      components.forEach(component => {
        const types = component.types;

        if (types.includes("street_number")) {
          addressData.street_number = component.longName;
        }
        if (types.includes("route")) {
          addressData.route = component.longName;
        }
        if (types.includes("locality")) {
          addressData.city = component.longName;
        }
        if (types.includes("administrative_area_level_1")) {
          addressData.state = component.shortName;
        }
        if (types.includes("postal_code")) {
          addressData.postal_code = component.longName;
        }
        if (types.includes("country")) {
          addressData.country = component.shortName;
        }
        if (
          types.includes("sublocality") ||
          types.includes("sublocality_level_1")
        ) {
          if (!addressData.city) {
            addressData.city = component.longName;
          }
        }
        if (types.includes("administrative_area_level_2")) {
          if (!addressData.city) {
            addressData.city = component.longName;
          }
        }
      });

      // Combine street number and route
      if (addressData.street_number && addressData.route) {
        addressData.street1 = `${addressData.street_number} ${addressData.route}`;
      } else if (addressData.route) {
        addressData.street1 = addressData.route;
      }

      return addressData;
    },

    clear() {
      this.searchQuery = "";
      this.predictions = [];
      this.showPredictions = false;
      this.$emit("input", "");
    }
  }
};
</script>

<style scoped>
.google-address-search {
  position: relative;
  width: 100%;
}

.field-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
}

.search-wrapper {
  position: relative;
  width: 100%;
}

.search-input {
  width: 100%;
  padding-right: 2.5rem;
}

.search-spinner {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  animation: spin 1s linear infinite;
  pointer-events: none;
}

.spinner-circle {
  stroke: #fa3434;
  stroke-dasharray: 50;
  stroke-dashoffset: 25;
}

@keyframes spin {
  to {
    transform: translateY(-50%) rotate(360deg);
  }
}

.predictions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  margin-top: 0.25rem;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
}

.prediction-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #f3f4f6;
}

.prediction-item:last-child {
  border-bottom: none;
}

.prediction-item:hover {
  background-color: #fef2f2;
}

.location-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  color: #fa3434;
  margin-top: 0.125rem;
}

.prediction-text {
  flex: 1;
  min-width: 0;
}

.main-text {
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
  margin-bottom: 0.125rem;
}

.secondary-text {
  color: #6b7280;
  font-size: 0.75rem;
}
</style>
